import { useLocation } from '@umijs/max';
import { useCallback, useMemo, useState } from 'react';

const CACHE_PREFIX = 'page-state:';

function readCache(key: string): Record<string, any> | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(key: string, value: Record<string, any>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function removeCache(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

export function usePageState<
  T extends Record<string, any> = Record<string, any>,
>(suffix?: string) {
  const location = useLocation();

  const cacheKey = useMemo(() => {
    const base = `${CACHE_PREFIX}${location.pathname}`;
    return suffix ? `${base}:${suffix}` : base;
  }, [location.pathname, suffix]);

  const cached = useMemo(() => readCache(cacheKey), [cacheKey]);

  const [pageState, setPageStateRaw] = useState<T>(
    () => (cached as T) ?? ({} as T),
  );

  const setPageState = useCallback(
    (value: Partial<T> | ((prev: T) => Partial<T>)) => {
      setPageStateRaw((prev) => {
        const next =
          typeof value === 'function'
            ? { ...prev, ...value(prev) }
            : { ...prev, ...value };
        writeCache(cacheKey, next);
        return next;
      });
    },
    [cacheKey],
  );

  const formProps = useMemo(
    () => ({
      initialValues: cached ?? undefined,
    }),
    [cached],
  );

  const onSubmit = useCallback(
    (params: Record<string, any>) => {
      const values = { ...params };
      delete values.current;
      delete values.pageSize;
      writeCache(cacheKey, values);
    },
    [cacheKey],
  );

  const onReset = useCallback(() => {
    removeCache(cacheKey);
  }, [cacheKey]);

  return {
    pageState,
    setPageState,
    formProps,
    onSubmit,
    onReset,
  };
}

export function clearAllPageState(): void {
  const keys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(CACHE_PREFIX)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    sessionStorage.removeItem(key);
  }
}

export default usePageState;
